import { Injectable, inject } from '@angular/core';
import { AccountInfo, BrowserAuthErrorCodes } from '@azure/msal-browser';
import { MsalService } from '@azure/msal-angular';
import { AuthInteractionInProgressError, AuthProviderPort } from '@domain/ports';
import { environment } from '@environment';
import {
  buildAuthScopes,
  buildAbsoluteUrl,
  buildLoginRequest,
  isMsalConfigured
} from './msal.runtime';

@Injectable({ providedIn: 'root' })
export class MsalAuthProvider extends AuthProviderPort {
  private readonly msal = inject(MsalService, { optional: true });
  private loginPromise: Promise<void> | null = null;

  isConfigured(): boolean {
    return isMsalConfigured() && Boolean(this.msal);
  }

  async initialize(): Promise<boolean> {
    if (!this.isConfigured() || !this.msal) return false;

    await this.msal.instance.initialize();

    const result = await this.msal.instance.handleRedirectPromise();
    if (result?.account) {
      this.msal.instance.setActiveAccount(result.account);
      return true;
    }

    const cached = this.msal.instance.getActiveAccount()
      ?? this.msal.instance.getAllAccounts()[0]
      ?? null;
    if (!cached) return false;

    this.msal.instance.setActiveAccount(cached);

    // A cached account from a previous session may have an expired refresh
    // token or correspond to a B2C policy that rejects `prompt=none`. If we
    // trust it blindly, the MsalInterceptor will later call
    // acquireTokenSilent against a dead session, hang on the hidden iframe,
    // and deadlock anything that awaits the HTTP call (login button stuck on
    // "Procesando..."). Verify the session up front and clear the account
    // when it's stale so the next login() drives a fresh redirect instead.
    try {
      await this.msal.instance.acquireTokenSilent({ account: cached, scopes: buildAuthScopes() });
      return true;
    } catch {
      await this.clearStaleAccount(cached);
      return false;
    }
  }

  private async clearStaleAccount(account: AccountInfo): Promise<void> {
    if (!this.msal) return;
    this.msal.instance.setActiveAccount(null);
    try {
      await this.msal.instance.clearCache({ account });
    } catch {
      // Best-effort: a partial cache clean still avoids the deadlock on the
      // next login, because setActiveAccount(null) already drops the stale
      // session from MSAL's in-memory state.
    }
  }

  async login(): Promise<void> {
    if (!this.isConfigured() || !this.msal) return;

    this.loginPromise ??= this.loginRedirect().finally(() => {
      this.loginPromise = null;
    });

    await this.loginPromise;
  }

  async logout(): Promise<boolean> {
    if (!this.isConfigured() || !this.msal) return false;

    await this.msal.instance.logoutRedirect({
      authority: environment.msal.authority || undefined,
      postLogoutRedirectUri: buildAbsoluteUrl(environment.msal.postLogoutRedirectPath)
    });

    return true;
  }

  private async loginRedirect(): Promise<void> {
    if (!this.msal) return;

    try {
      await this.msal.instance.loginRedirect(buildLoginRequest());
    } catch (error) {
      if (!this.isInteractionInProgress(error)) throw error;

      if (!this.canRecoverStaleRedirect()) {
        throw new AuthInteractionInProgressError();
      }

      this.clearStaleInteractionStatus();
      await this.msal.instance.loginRedirect(buildLoginRequest());
    }
  }

  private isInteractionInProgress(error: unknown): boolean {
    return typeof error === 'object'
      && error !== null
      && 'errorCode' in error
      && error.errorCode === BrowserAuthErrorCodes.interactionInProgress;
  }

  private canRecoverStaleRedirect(): boolean {
    const hash = window.location.hash;
    return !hash.includes('code=') && !hash.includes('id_token=') && !hash.includes('error=');
  }

  private clearStaleInteractionStatus(): void {
    const key = 'msal.interaction.status';
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  }
}
